import { Component, useContext, type ErrorInfo, type ReactNode } from 'react';
import { EmptyState } from './EmptyState';
import { I18nContext } from '@/shared/i18n/I18nProvider';
import { MESSAGES, type MessageKey } from '@/shared/i18n/messages';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

/** Catches render failures (including lazy-chunk load errors) so a broken
 *  module never leaves the user staring at a blank page. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) return <ErrorFallback />;
    return this.props.children;
  }
}

function ErrorFallback() {
  const context = useContext(I18nContext);
  // Keep the fallback renderable even if the failure happened while the i18n
  // provider was mounting or recovering. The normal path remains localized.
  const t = context?.t ?? ((key: MessageKey) => MESSAGES.en[key] ?? key);
  return (
    <div className="p-4">
      <EmptyState
        title={t('common.errorTitle')}
        body={t('common.errorBody')}
        action={
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="tap-target inline-flex items-center rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover"
          >
            {t('common.retry')}
          </button>
        }
      />
    </div>
  );
}
